import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://scalemicrofin.scalebot.in';
console.log('Backend URL:', BACKEND_URL);
const API_URL = `${BACKEND_URL}/api`;
console.log('API URL:', API_URL);

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if the request explicitly asked to skip auth redirect
      if (error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }

      // Only auto-redirect if we're not already on login page
      // This prevents aggressive logouts during checkAuth
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  auth: {
    login: async (data) => {
      const res = await axiosInstance.post('/auth/login', data);
      return res.data;
    },
    setup2FA: async (token) => {
      const res = await axiosInstance.post('/auth/2fa/setup', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data;
    },
    verify2FA: async (data, token) => {
      const res = await axiosInstance.post('/auth/2fa/verify', data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data;
    },
    register: async (data) => {
      const res = await axiosInstance.post('/auth/register', data);
      return res.data;
    },
    requestMagicLink: async (data) => {
      const res = await axiosInstance.post('/auth/magic-link', data);
      return res.data;
    },
    googleLogin: async (accessToken) => {
      const res = await axiosInstance.post('/auth/google-login', { access_token: accessToken });
      return res.data;
    },
    getMe: async () => {
      const res = await axiosInstance.get('/auth/me', { skipAuthRedirect: true });
      return res.data;
    },
  },

  // Mandali
  mandali: {
    getAll: async () => {
      const res = await axiosInstance.get('/mandali');
      return res.data;
    },
    create: async (data) => {
      const res = await axiosInstance.post('/mandali', data);
      return res.data;
    },
  },

  // Account Holders
  accountHolders: {
    getMyAccounts: async () => {
      const res = await axiosInstance.get('/account-holder/me');
      return res.data;
    },
    getMyTransactions: async () => {
      const res = await axiosInstance.get('/account-holder/transactions');
      return res.data;
    },
    getAll: async (params) => {
      const res = await axiosInstance.get('/account-holders', { params });
      return res.data;
    },
    getOne: async (id) => {
      const res = await axiosInstance.get(`/account-holders/${id}`);
      return res.data;
    },
    getTransactions: async (id, txType = 'all', dateRange = '3m') => {
      const res = await axiosInstance.get(`/account-holders/${id}/transactions`, {
        params: { tx_type: txType, date_range: dateRange }
      });
      return res.data;
    },
    create: async (data) => {
      const res = await axiosInstance.post('/account-holders', data);
      return res.data;
    },
    update: async (id, data) => {
      const res = await axiosInstance.put(`/account-holders/${id}`, data);
      return res.data;
    },
    delete: async (id, reason) => {
      const res = await axiosInstance.delete(`/account-holders/${id}`, { params: { reason } });
      return res.data;
    },
    toggleRestriction: async (id, isRestricted, reason) => {
      const res = await axiosInstance.put(`/account-holders/${id}/restriction`, { is_restricted: isRestricted, reason });
      return res.data;
    },
  },

  // Loans
  loans: {
    getAll: async (params) => {
      const res = await axiosInstance.get('/loans', { params });
      return res.data;
    },
    getOne: async (id) => {
      const res = await axiosInstance.get(`/loans/${id}`);
      return res.data;
    },
    apply: async (data) => {
      const res = await axiosInstance.post('/loans/apply', data);
      return res.data;
    },
    approve: async (loanId) => {
      const res = await axiosInstance.post(`/loans/${loanId}/approve`);
      return res.data;
    },
    getSchedule: async (loanId) => {
      const res = await axiosInstance.get(`/loans/${loanId}/schedule`);
      return res.data;
    },
    recordPayment: async (loanId, paymentData) => {
      const res = await axiosInstance.post(`/loans/${loanId}/record-payment`, paymentData);
      return res.data;
    },
    getPayments: async (loanId) => {
      const res = await axiosInstance.get(`/loans/${loanId}/payments`);
      return res.data;
    },
    closeEarly: async (loanId, data) => {
      const res = await axiosInstance.post(`/loans/${loanId}/close-early`, {
        payment_date: data.payment_date,
        payment_mode: data.payment_mode,
        notes: data.notes || 'Early loan closure'
      });
      return res.data;
    },
    reschedule: async (loanId, data) => {
      const res = await axiosInstance.post(`/loans/${loanId}/reschedule`, data);
      return res.data;
    },
    downloadAgreement: async (loanId) => {
      const res = await axiosInstance.get(`/loans/${loanId}/agreement`, {
        responseType: 'blob'
      });
      return res;
    },
    updateApplicationDate: async (loanId, data) => {
      const res = await axiosInstance.patch(`/loans/${loanId}/application-date`, data);
      return res.data;
    },
  },

  // Dashboard
  dashboard: {
    getStats: async (params) => {
      const res = await axiosInstance.get('/dashboard/stats', { params });
      return res.data;
    },
    getActivity: async (params) => {
      const res = await axiosInstance.get('/dashboard/activity', { params });
      return res.data;
    },
  },

  // Collections
  collections: {
    getMembers: async (filterType = 'due_this_month', search = null) => {
      const params = { filter_type: filterType };
      if (search) params.search = search;
      const res = await axiosInstance.get('/collections/members', { params });
      return res.data;
    },
  },

  // Reports
  reports: {
    getMonthlyCollection: async (month, mandaliId = null) => {
      const params = { month };
      if (mandaliId) params.mandali_id = mandaliId;
      const res = await axiosInstance.get('/reports/monthly-collection', { params });
      return res.data;
    },
    getOverdueLoans: async (mandaliId = null) => {
      const params = {};
      if (mandaliId) params.mandali_id = mandaliId;
      const res = await axiosInstance.get('/reports/overdue-loans', { params });
      return res.data;
    },
  },

  // Interest Distribution
  interest: {
    calculate: async (month, year, mandaliId = null) => {
      const params = { month, year };
      if (mandaliId) params.mandali_id = mandaliId;
      const res = await axiosInstance.get('/interest/calculate', { params });
      return res.data;
    },
    distribute: async (data) => {
      const res = await axiosInstance.post('/interest/distribute', data);
      return res.data;
    },
  },

  // Lavajam (formerly Recurring)
  lavajam: {
    getAll: async (params) => {
      const res = await axiosInstance.get('/lavajam/all', { params });
      return res.data;
    },
    getOne: async (id) => {
      const res = await axiosInstance.get(`/lavajam/${id}`);
      return res.data;
    },
    create: async (data) => {
      const res = await axiosInstance.post('/lavajam/create', data);
      return res.data;
    },
    close: async (id) => {
      const res = await axiosInstance.patch(`/lavajam/${id}/close`);
      return res.data;
    },
    getMemberAccounts: async (memberId) => {
      const res = await axiosInstance.get(`/lavajam/member/${memberId}`);
      return res.data;
    },
    recordPayment: async (id, data) => {
      const res = await axiosInstance.post(`/lavajam/${id}/payment`, data);
      return res.data;
    },
    getPayments: async (id) => {
      const res = await axiosInstance.get(`/lavajam/${id}/payments`);
      return res.data;
    },
    getPending: async (id) => {
      const res = await axiosInstance.get(`/lavajam/${id}/pending`);
      return res.data;
    },
    updatePayment: async (paymentId, data) => {
      const res = await axiosInstance.patch(`/lavajam/payment/${paymentId}`, data);
      return res.data;
    },
    getStats: async () => {
      const res = await axiosInstance.get('/lavajam/stats');
      return res.data;
    },
    previewBackfill: async (data) => {
      const res = await axiosInstance.post('/lavajam/preview-backfill', data);
      return res.data;
    },
    syncHistory: async (id, data) => {
      const res = await axiosInstance.post(`/lavajam/${id}/sync-history`, data);
      return res.data;
    }
  },

  // Search
  search: {
    all: async (query) => {
      const res = await axiosInstance.get('/search', { params: { q: query } });
      return res.data;
    },
  },
};

export default axiosInstance;
